import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, BookMarked, Target, CheckCircle2, Trash2, Plus, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Progress = ({ value }) => (
  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
    <div className="h-full bg-[#166534] transition-all" style={{ width: `${Math.min(100, value)}%` }} />
  </div>
);

export default function CoinLecturePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStudent = user?.role === "student";
  const canManageGoals = ["teacher", "scolarite", "admin", "super_admin", "director", "founder"].includes(user?.role);
  const [data, setData] = useState({ readings: [], in_progress: 0, finished: 0 });
  const [goals, setGoals] = useState([]);
  const [classes, setClasses] = useState([]);
  const [pageDlg, setPageDlg] = useState(null); // { reading, page }
  const [goalDlg, setGoalDlg] = useState(false);
  const [goalForm, setGoalForm] = useState({ class_name: "", title: "", books_target: 3, period: "" });
  const [classProg, setClassProg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/reading/my").then(({ data }) => setData(data)).catch(() => {});
    api.get("/reading/goals").then(({ data }) => setGoals(data)).catch(() => {});
    if (canManageGoals) api.get("/classes").then(({ data }) => setClasses(data)).catch(() => {});
  };
  useEffect(load, []);

  const savePage = async () => {
    setSaving(true);
    try {
      await api.post("/reading/progress", {
        item_id: pageDlg.reading.item_id,
        current_page: parseInt(pageDlg.page, 10) || 1,
        total_pages: pageDlg.total ? parseInt(pageDlg.total, 10) : undefined,
        status: pageDlg.finish ? "termine" : "en_cours",
      });
      toast.success(pageDlg.finish ? "Bravo, livre terminé ! 🎉" : "Progression enregistrée ✓");
      setPageDlg(null); load();
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const createGoal = async () => {
    if (!goalForm.class_name || !goalForm.title) { toast.error("Classe et titre obligatoires"); return; }
    setSaving(true);
    try {
      await api.post("/reading/goals", { ...goalForm, books_target: parseInt(goalForm.books_target, 10) || 1 });
      toast.success("Objectif de lecture publié ✓");
      setGoalDlg(false); setGoalForm({ class_name: "", title: "", books_target: 3, period: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const viewClass = async (cls) => {
    try {
      const { data } = await api.get(`/reading/class-progress/${encodeURIComponent(cls)}`);
      setClassProg({ cls, rows: data });
    } catch { toast.error("Erreur de chargement"); }
  };

  const enCours = data.readings.filter(r => r.status === "en_cours");
  const termines = data.readings.filter(r => r.status === "termine");

  return (
    <div className="space-y-6" data-testid="coin-lecture-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><BookMarked className="h-3 w-3" /><span>COIN LECTURE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">{isStudent ? "Ma lecture en cours" : "Coin lecture — objectifs de classe"}</h1>
          <p className="text-slate-500 mt-1">{isStudent ? "Reprenez vos livres là où vous vous êtes arrêté et suivez vos objectifs de classe." : "Publiez des objectifs de lecture par classe et suivez la progression des élèves."}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/dashboard/bibliotheque")} variant="outline" className="gap-1 border-[#1E3A8A] text-[#1E3A8A]" data-testid="go-library-btn">
            <BookOpen className="h-4 w-4" />Choisir un livre
          </Button>
          {canManageGoals && (
            <Button onClick={() => setGoalDlg(true)} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-goal-btn">
              <Plus className="h-4 w-4" />Nouvel objectif
            </Button>
          )}
        </div>
      </div>

      {/* STATISTIQUES PERSONNELLES (élève) */}
      {isStudent && (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
          <div className="text-3xl font-extrabold text-[#EA580C]">{data.in_progress}</div>
          <div className="text-xs uppercase font-bold text-slate-500 mt-1">En cours</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
          <div className="text-3xl font-extrabold text-[#166534]">{data.finished}</div>
          <div className="text-xs uppercase font-bold text-slate-500 mt-1">Livres terminés</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center col-span-2 md:col-span-1">
          <div className="text-3xl font-extrabold text-[#1E3A8A]">{goals.length}</div>
          <div className="text-xs uppercase font-bold text-slate-500 mt-1">Objectifs actifs</div>
        </div>
      </div>
      )}

      {/* OBJECTIFS */}
      {goals.length > 0 && (
        <div className="space-y-3" data-testid="goals-section">
          <h2 className="font-heading font-bold text-xl text-[#1E3A8A] flex items-center gap-2"><Target className="h-5 w-5" />Objectifs de lecture{isStudent ? " de ma classe" : ""}</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {goals.map(g => {
              const done = g.my_finished ?? null;
              const pct = done !== null ? (done / g.books_target) * 100 : null;
              return (
                <div key={g.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`goal-${g.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-heading font-bold text-[#1E3A8A]">{g.title}</div>
                      <div className="text-xs text-slate-500">{g.class_name}{g.period ? ` · ${g.period}` : ""} — {g.books_target} livre(s) à lire</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {canManageGoals && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => viewClass(g.class_name)} data-testid={`goal-class-${g.id}`}><Trophy className="h-3.5 w-3.5 text-[#EA580C]" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => { await api.delete(`/reading/goals/${g.id}`); load(); }} data-testid={`goal-del-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                  {pct !== null && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold">{done}/{g.books_target} terminés</span>
                        <span className={pct >= 100 ? "text-[#166534] font-bold" : "text-slate-500"}>{pct >= 100 ? "Objectif atteint ! 🎉" : `${Math.round(pct)}%`}</span>
                      </div>
                      <Progress value={pct} />
                    </div>
                  )}
                  {g.description && <p className="text-xs text-slate-500 mt-2">{g.description}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LECTURES EN COURS (élève) */}
      {isStudent && (
      <div className="space-y-3">
        <h2 className="font-heading font-bold text-xl text-[#1E3A8A] flex items-center gap-2"><BookOpen className="h-5 w-5" />En cours ({enCours.length})</h2>
        {enCours.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-400" data-testid="no-readings">
            Aucune lecture en cours. Ouvrez un livre depuis la <button className="text-[#1E3A8A] font-semibold underline" onClick={() => navigate("/dashboard/bibliotheque")}>Bibliothèque</button> pour commencer !
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {enCours.map(r => {
              const pct = r.total_pages ? (r.current_page / r.total_pages) * 100 : null;
              return (
                <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`reading-${r.item_id}`}>
                  <div className="font-heading font-bold text-[#1E3A8A] leading-tight">{r.item_title}</div>
                  <div className="text-xs text-slate-500">{r.item_author}{r.item_category ? ` · ${r.item_category}` : ""}</div>
                  <div className="mt-2 text-sm">
                    <span className="font-bold text-[#EA580C]">Page {r.current_page}</span>
                    {r.total_pages ? <span className="text-slate-400"> / {r.total_pages}</span> : null}
                  </div>
                  {pct !== null && <div className="mt-1"><Progress value={pct} /></div>}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="bg-[#166534] text-white gap-1 h-8 text-xs flex-1"
                      onClick={() => setPageDlg({ reading: r, page: r.current_page, total: r.total_pages || "" })}
                      data-testid={`resume-${r.item_id}`}>
                      <BookMarked className="h-3.5 w-3.5" />Mettre à jour ma page
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1 border-[#166534] text-[#166534]"
                      onClick={() => setPageDlg({ reading: r, page: r.current_page, total: r.total_pages || "", finish: true })}
                      data-testid={`finish-${r.item_id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />Terminé
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* TERMINÉS */}
      {isStudent && termines.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-bold text-xl text-[#166534] flex items-center gap-2"><CheckCircle2 className="h-5 w-5" />Terminés ({termines.length})</h2>
          <div className="flex flex-wrap gap-2">
            {termines.map(r => (
              <span key={r.id} className="bg-green-50 border border-green-200 text-green-800 text-xs font-semibold rounded-full px-3 py-1.5">
                {r.item_title}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* DIALOG PAGE */}
      <Dialog open={!!pageDlg} onOpenChange={(o) => !o && setPageDlg(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{pageDlg?.finish ? "Marquer comme terminé" : "Reprendre ma lecture"}</DialogTitle></DialogHeader>
          {pageDlg && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 font-semibold">{pageDlg.reading.item_title}</p>
              {!pageDlg.finish && (
                <>
                  <div>
                    <Label>Je suis à la page</Label>
                    <Input type="number" min="1" value={pageDlg.page} onChange={e => setPageDlg({ ...pageDlg, page: e.target.value })} data-testid="page-input" />
                  </div>
                  <div>
                    <Label>Nombre total de pages (optionnel)</Label>
                    <Input type="number" min="1" value={pageDlg.total} onChange={e => setPageDlg({ ...pageDlg, total: e.target.value })} data-testid="total-pages-input" />
                  </div>
                </>
              )}
              {pageDlg.finish && <p className="text-sm text-slate-500">Ce livre sera compté dans vos objectifs de lecture. Félicitations !</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPageDlg(null)}>Annuler</Button>
            <Button onClick={savePage} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="save-page-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG OBJECTIF */}
      <Dialog open={goalDlg} onOpenChange={setGoalDlg}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Nouvel objectif de lecture</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Classe *</Label>
              <Input list="goal-classes" value={goalForm.class_name} onChange={e => setGoalForm({ ...goalForm, class_name: e.target.value })} placeholder="Ex : Primaire CE2" data-testid="goal-class-input" />
              <datalist id="goal-classes">{classes.map(c => <option key={c.id} value={c.name} />)}</datalist>
            </div>
            <div>
              <Label>Titre de l&apos;objectif *</Label>
              <Input value={goalForm.title} onChange={e => setGoalForm({ ...goalForm, title: e.target.value })} placeholder="Ex : Défi lecture du trimestre" data-testid="goal-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Livres à lire *</Label>
                <Input type="number" min="1" value={goalForm.books_target} onChange={e => setGoalForm({ ...goalForm, books_target: e.target.value })} data-testid="goal-target-input" />
              </div>
              <div>
                <Label>Période</Label>
                <Input value={goalForm.period} onChange={e => setGoalForm({ ...goalForm, period: e.target.value })} placeholder="Trimestre 1" data-testid="goal-period-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalDlg(false)}>Annuler</Button>
            <Button onClick={createGoal} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="goal-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG PROGRESSION CLASSE */}
      <Dialog open={!!classProg} onOpenChange={(o) => !o && setClassProg(null)}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Lecture — {classProg?.cls}</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {(classProg?.rows || []).length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Aucun élève.</p> :
              classProg.rows.map((s, i) => (
                <div key={s.student_id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
                  <span>{i < 3 && s.finished > 0 ? ["🥇", "🥈", "🥉"][i] + " " : ""}{s.name}</span>
                  <span className="tabular-nums text-slate-500">{s.finished} terminé(s) · {s.in_progress} en cours</span>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
